import {undef} from './undef';
import {allowFieldAutoProgress, allowInputFieldErrorHighlighting} from './field';
import {dateUtils} from '@homeaway/react-date-pickers';

function getFormattedDate(date) {
    return date.toISOString().slice(0, 10);
}

function getNightsCount(checkIn, checkOut) {
    const checkInDate = new Date(checkIn).setHours(0, 0, 0, 0);
    const checkOutDate = new Date(checkOut).setHours(0, 0, 0, 0);

    return Math.round((checkOutDate - checkInDate) / (24 * 60 * 60 * 1000));
}

function getNightsCountLabel(startDate, endDate, nightsText) {
    if (!startDate || !endDate || endDate <= startDate) {
        return '';
    }

    const numberOfNights = getNightsCount(startDate, endDate);

    const message = ` ${nightsText}${numberOfNights} `;
    return message;
}

function getDatesSummaryLabel(startDate, endDate, locale) {
    if (!startDate || !endDate || endDate <= startDate) {
        return '';
    }

    const checkIn = startDate.split('-');
    const checkOut = endDate.split('-');
    const isSameBookingYear = checkIn[0] === checkOut[0];
    const format = {month: 'short'};
    const checkInMonth = dateUtils.getLocaleDateString(startDate, locale, format);
    const checkOutMonth = dateUtils.getLocaleDateString(endDate, locale, format);

    if (!isSameBookingYear) {
        const message = `${checkInMonth} ${checkIn[2]}, ${checkIn[0]} - ${checkOutMonth} ${checkOut[2]}, ${checkOut[0]}`;
        return message;
    }
    const message = `${checkInMonth} ${checkIn[2]} - ${checkOutMonth} ${checkOut[2]}`;
    return message;
}

function getDatesSummaryAndNightCountLabel(startDate, endDate, locale, nightsText) {
    if (!startDate || !endDate || endDate <= startDate) {
        return '';
    }

    return `${getDatesSummaryLabel(startDate, endDate, locale)} (${ getNightsCountLabel(startDate, endDate, nightsText)})`;
}

function hasEmptyDate(date) {
    return !date;
}

function hasEmptyDates(startDate, endDate) {
    return hasEmptyDate(startDate) && hasEmptyDate(endDate);
}

function hasEmptyDatesValues({startDate, endDate}) {
    return hasEmptyDates(startDate, endDate);
}

function hasFilledDate(date) {
    return !hasEmptyDate(date);
}

function hasFilledDates(startDate, endDate) {
    return hasFilledDate(startDate) && hasFilledDate(endDate);
}

function hasFilledDatesValues({startDate, endDate}) {
    return hasFilledDates(startDate, endDate);
}

function hasPartialDates(startDate, endDate) {
    return !hasEmptyDates(startDate, endDate) && !hasFilledDates(startDate, endDate);
}

function hasPartialDatesValues({startDate, endDate}) {
    return hasPartialDates(startDate, endDate);
}

function hasValidDate(date) {
    return hasFilledDate(date);
}

function hasValidDateRange(startDate, endDate) {
    const startTimestamp = Date.parse(startDate);
    const endTimestamp = Date.parse(endDate);

    const todayDate = getFormattedDate(new Date());
    const todayTimestamp = Date.parse(todayDate);

    return (
        startTimestamp >= todayTimestamp &&
        startTimestamp < endTimestamp
    );
}

function hasValidDates(startDate, endDate) {
    return hasValidDate(startDate) && hasValidDate(endDate) && hasValidDateRange(startDate, endDate);
}

function hasValidDatesValues({startDate, endDate}) {
    return hasValidDates(startDate, endDate);
}

function hasDatesDayTemplateComponent(datesOptions) {
    return !!datesOptions.dayTemplateComponent;
}

function getDatesLimitDayDataId(date) {
    return hasFilledDate(date) ? `${date}` : '';
}

function getDatesSelectedDayDataId({startDate, endDate}) {
    return (
        hasFilledDate(startDate) &&
        hasEmptyDate(endDate)
            ? `${startDate}`
            : ''
    );
}

function getDatesDayDataIds({startDate, endDate}) {
    const startDayDataId = getDatesLimitDayDataId(startDate);
    const endDayDataId = getDatesLimitDayDataId(endDate);

    const selectedDayDataId = getDatesSelectedDayDataId({startDate, endDate});

    return {
        startDayDataId,
        endDayDataId,
        selectedDayDataId
    };
}

function getDatesSelectDateHintDayData({dayData, prevDayDataId, nextDayDataId}) {
    return {
        ...dayData,
        [prevDayDataId]: {
            ...dayData[prevDayDataId],
            showSelectDateHint: false
        },
        [nextDayDataId]: {
            ...dayData[nextDayDataId],
            showSelectDateHint: true
        }
    };
}

function shouldDatesFieldAutoProgressOnClose(mediaSettings, formOptions, datesOptions, latestDatesValues) {
    const {
        isMobile
    } = mediaSettings;

    return (
        isMobile &&
        allowFieldAutoProgress(formOptions, datesOptions) &&
        hasFilledDatesValues(latestDatesValues)
    );
}

function shouldDatesFieldAutoProgressOnSubmit(mediaSettings, formOptions, datesOptions, latestDatesValues) {
    const {
        isMobile
    } = mediaSettings;

    // TODO: Fix on SHCMP-430
    /* istanbul ignore next */
    return (
        !isMobile &&
        allowFieldAutoProgress(formOptions, datesOptions) &&
        hasFilledDatesValues(latestDatesValues)
    );
}

function shouldDatesFieldNotAutoProgressOnCancel(mediaSettings, formOptions, datesOptions) {
    const {
        isMobile
    } = mediaSettings;

    return (
        isMobile &&
        allowFieldAutoProgress(formOptions, datesOptions)
    );
}

function showDatesInputFieldError(mediaSettings, formOptions, datesOptions, latestDatesValues) {
    const {
        isMobile
    } = mediaSettings;

    return !isMobile && (
        allowInputFieldErrorHighlighting(formOptions, datesOptions) && !hasValidDatesValues(latestDatesValues)
    );
}

function showDatesInputIcon(formOptions, datesOptions, id) {
    const showInputIconKey = `showInputIcon${id}`;

    const {
        showIcons
    } = formOptions;

    const {
        [showInputIconKey]: showInputIcon
    } = datesOptions;

    return (
        showInputIcon ||
        showIcons && showInputIcon === undef
    );
}

function showDatesInputIcon1(formOptions, datesOptions) {
    return showDatesInputIcon(formOptions, datesOptions, 1);
}

function showDatesInputIcon2(formOptions, datesOptions) {
    return showDatesInputIcon(formOptions, datesOptions, 2);
}

function getDatesPickerModalTitleMessage(datesTranslations, datesValues) {
    const {
        modalTitleStartDateMessage,
        modalTitleEndDateMessage
    } = datesTranslations;

    const {
        startDate,
        endDate
    } = datesValues;

    return (
        hasEmptyDate(startDate) && modalTitleStartDateMessage ||
        hasEmptyDate(endDate) && modalTitleEndDateMessage
    ) || null;
}

export {
    getFormattedDate,
    getNightsCount,
    getNightsCountLabel,
    getDatesSummaryLabel,
    getDatesSummaryAndNightCountLabel,
    hasEmptyDate,
    hasEmptyDates,
    hasEmptyDatesValues,
    hasFilledDate,
    hasFilledDates,
    hasFilledDatesValues,
    hasPartialDates,
    hasPartialDatesValues,
    hasValidDate,
    hasValidDateRange,
    hasValidDates,
    hasValidDatesValues,
    hasDatesDayTemplateComponent,
    getDatesLimitDayDataId,
    getDatesSelectedDayDataId,
    getDatesDayDataIds,
    getDatesSelectDateHintDayData,
    shouldDatesFieldAutoProgressOnClose,
    shouldDatesFieldAutoProgressOnSubmit,
    shouldDatesFieldNotAutoProgressOnCancel,
    showDatesInputFieldError,
    showDatesInputIcon,
    showDatesInputIcon1,
    showDatesInputIcon2,
    getDatesPickerModalTitleMessage
};
